import type { Field } from '../fields/Field';
import { ENUMS, type ISpecEnums } from '../spec/SpecEnums';
import type { FIXValue } from '../util/util';
import { EnumType } from './EnumType';

/**
 * Class for managing FIX enumerations.
 * Provides functionality to process and look up FIX field enumerations
 * using both tag-value pairs and tag-only lookups.
 */
export class Enums {
    /** Array of all available enumeration specifications */
    public enums: ISpecEnums[] = ENUMS;
    /** Cache map for looking up enumerations by tag and value */
    public cacheMap: Map<string, ISpecEnums> = new Map<string, ISpecEnums>();
    /** Cache map for looking up enumerations by tag only */
    public cacheMapTag: Map<string, ISpecEnums> = new Map<string, ISpecEnums>();

    /**
     * Creates a new Enums instance and initializes the cache maps
     * with all available enumeration specifications.
     */
    constructor() {
        this.enums.forEach((enumType: ISpecEnums) => {
            this.cacheMap.set(`${enumType.tag}|${enumType.value}`, enumType);
            this.cacheMapTag.set(`${enumType.tag}`, enumType);
        });
    }

    /**
     * Gets an enumeration by its tag and value.
     *
     * @param {string} tag - The tag number
     * @param {FIXValue} value - The enumeration value
     * @returns {ISpecEnums | undefined} The found enumeration, or undefined if not found
     */
    public getEnum(tag: string, value: FIXValue): ISpecEnums | undefined {
        return this.cacheMap.get(`${tag}|${value}`);
    }

    /**
     * Gets an enumeration by its tag only.
     *
     * @param {string} tag - The tag number
     * @returns {ISpecEnums | undefined} The found enumeration, or undefined if not found
     */
    public getEnumByTag(tag: string): ISpecEnums | undefined {
        return this.cacheMapTag.get(tag);
    }

    /**
     * Processes a field's enumeration, setting the appropriate enumeration type
     * if a matching enumeration is found.
     *
     * @param {Field} field - The field to process
     */
    public processEnum(field: Field): void {
        const enumTypes: EnumType = new EnumType();
        const foundEnum: ISpecEnums | undefined = this.cacheMap.get(`${field.tag}|${field.value}`);
        if (foundEnum) {
            enumTypes.setEnumeration(foundEnum);
            field.setEnumeration(enumTypes);
        }
    }
}
